<div class="discovery-citations-container">
  <div class="header">
    <h1>Discovery Citations</h1>
    <p class="subtitle">Rows are keyed by <strong>primary brand</strong> (category/region). Competitors (e.g. MG) appear nested under that row—use search to find a competitor, then open the primary-brand entry. Prompt-type counts (branded / generic / per-competitor AI Mode) are admin-only below.</p>
  </div>

  <div class="search-row">
    <mat-form-field appearance="outline" class="search-field">
      <mat-label>Search brand or competitor</mat-label>
      <input matInput [(ngModel)]="searchTerm" (keyup.enter)="applySearch()" placeholder="e.g. MG, Ford" />
    </mat-form-field>
    <button mat-raised-button color="primary" (click)="applySearch()">Search</button>
    @if (searchTerm) {
      <button mat-button (click)="searchTerm = ''; applySearch()">Clear</button>
    }
  </div>

  @if (loading) {
    <div class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading discovery citations...</p>
    </div>
  }

  @if (!loading) {
    <div class="accordion-container">
      <div class="list-header">
        <div class="list-info">
          <span>Total: {{ totalCount }}</span>
          <span>Page {{ currentPage }} of {{ totalPages || 1 }}</span>
        </div>
      </div>

      <mat-accordion class="citations-accordion">
        @for (citation of citations; track citation.id) {
          <mat-expansion-panel class="citation-panel" (expandedChange)="onPanelToggle(citation, $event)">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <div class="panel-header-content">
                  <div class="panel-header-main">
                    <span class="citation-id">#{{ citation.id }}</span>
                    <span class="citation-brand">{{ citation.brand }}</span>
                    <span class="citation-meta">{{ citation.category }} · {{ citation.region }}</span>
                    <!-- @if (citation.competitors_in_run?.length) {
                      <span class="competitors-in-run" matTooltip="Competitors included in this cached run (nested under primary brand)">
                        · Competitors: {{ (citation.competitors_in_run ?? []).join(', ') }}
                      </span>
                    } -->
                  </div>
                  <div class="panel-header-badges">
                    <span class="badge">{{ citation.citation_count }} items</span>
                    <span class="date-badge">{{ formatDate(citation.last_fetched_at) }}</span>
                  </div>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="panel-content">
              <div class="actions-section">
                <button
                  mat-raised-button
                  color="primary"
                  [matMenuTriggerFor]="exportMenu"
                  (click)="exportingCitation = citation"
                  matTooltip="Export this citation as CSV or formatted TXT">
                  <mat-icon>download</mat-icon>
                  Export
                  <mat-icon class="dropdown-arrow">arrow_drop_down</mat-icon>
                </button>
                <mat-menu #exportMenu="matMenu">
                  <button mat-menu-item (click)="exportCitationAsCsv(exportingCitation!)">
                    <span>CSV</span>
                  </button>
                  <button mat-menu-item (click)="exportCitationAsTxt(exportingCitation!)">
                    <span>TXT</span>
                  </button>
                </mat-menu>
                <button
                  mat-raised-button
                  color="warn"
                  matTooltip="Remove this stored citation"
                  (click)="deleteCitation(citation)">
                  <mat-icon>delete</mat-icon>
                  Delete
                </button>
              </div>

              <!-- Summary (project-wise, like Discovery Pages Management) -->
              <div class="content-section">
                <h3 class="section-title">Summary</h3>
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">ID:</span>
                    <span class="info-value">{{ citation.id }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Brand:</span>
                    <span class="info-value">{{ citation.brand }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Category:</span>
                    <span class="info-value">{{ citation.category }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Region:</span>
                    <span class="info-value">{{ citation.region }}</span>
                  </div>
                  <div class="info-item">
                    <!-- <span class="info-label">Base Domain:</span>
                    <span class="info-value">{{ displayBaseDomain(citation, citation.id) || 'N/A' }}</span> -->
                    <span class="info-label">Competitors:</span>
                    <span class="info-value">{{ (citation.competitors_in_run ?? []).length ? (citation.competitors_in_run ?? []).join(', ') : 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Cited items:</span>
                    <span class="info-value">{{ citation.citation_count }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Last Fetched:</span>
                    <span class="info-value">{{ formatDate(citation.last_fetched_at) }}</span>
                  </div>
                </div>
              </div>

              @if (loadingDetailId === citation.id) {
                <p class="loading-detail">Loading full record…</p>
              }

              @if (getDiscoverySections(citation.id); as ds) {
                <div class="content-section admin-prompt-breakdown">
                  <h3 class="section-title">Admin: prompt-type breakdown</h3>
                  <p class="section-subtitle">Validate primary branded vs generic AI Overview prompts and per-competitor AI Mode batches.</p>
                  @if (getCitationDetail(citation.id); as det) {
                    @if (citation.competitors_in_run?.length || competitorDiscoveryEntries(citation.id).length) {
                      @if (isCompetitorJobActivelyRunning(det)) {
                        <div class="competitor-discovery-banner competitor-discovery-running">
                          <mat-spinner diameter="22"></mat-spinner>
                          <div class="competitor-discovery-running-text">
                            <span>Loading competitor AI Mode prompts (same batch as bulk overview)…</span>
                            <span class="citation-job-id">Citation #{{ citation.id }} · server job active</span>
                            @if (analysisBulkTaskId || analysisLlmTaskId) {
                            <span class="parallel-task-ids">
                              @if (analysisBulkTaskId) { <span>Bulk task: <code>{{ analysisBulkTaskId }}</code></span> }
                              @if (analysisLlmTaskId) { <span>LLM visibility: <code>{{ analysisLlmTaskId }}</code></span> }
                            </span>
                            }
                          </div>
                          @if (showCompetitorStopButton(citation)) {
                            <button mat-stroked-button type="button" color="warn" (click)="stopCompetitorLoad(citation)">Stop</button>
                          }
                        </div>
                      }
                      @if (showCompetitorResumeRow(citation)) {
                        <div class="competitor-discovery-banner competitor-discovery-resume">
                          <button mat-stroked-button type="button" color="primary" (click)="loadCompetitorDiscoveryData(citation)">
                            <mat-icon>refresh</mat-icon>
                            Load competitor discovery data
                          </button>
                          <span class="competitor-discovery-hint">
                            Runs the same per-competitor discovery batch used on bulk overview when primary data already exists.
                          </span>
                        </div>
                      }
                    }
                  }
                  <ul class="breakdown-list">
                    <li><strong>Primary branded (AI Overview):</strong> {{ ($any(ds['primary_branded'])?.length) || 0 }} prompts</li>
                    <li><strong>Generic / non-branded:</strong> {{ ($any(ds['generic'])?.length) || 0 }} prompts</li>
                    @for (kv of competitorDiscoveryEntries(citation.id); track kv[0]) {
                      <li><strong>Competitor {{ kv[0] }}:</strong> {{ kv[1].length }} AI Mode prompts</li>
                    }
                  </ul>
                </div>
              }

              @if (getCitationDetail(citation.id)) {
                @if (getDiscoveryPromptAccordionItems(citation.id).length > 0) {
                <div class="content-section discovery-prompt-accordion-section">
                  <div class="section-header discovery-prompt-accordion-header">
                    <div>
                      <h3 class="section-title">Prompts, responses &amp; citations</h3>
                      <p class="section-subtitle">Expand each prompt to preview the AI response, open full text, and open source links.</p>
                    </div>
                    <button
                      mat-stroked-button
                      color="primary"
                      matTooltip="Copy all prompts, responses and citation URLs"
                      (click)="copyCitedContent(citation.id)">
                      <mat-icon>content_copy</mat-icon>
                      Copy all
                    </button>
                  </div>
                  <mat-accordion class="discovery-prompt-accordion" multi="true">
                    @for (item of getDiscoveryPromptAccordionItems(citation.id); track item.trackKey) {
                      <mat-expansion-panel class="discovery-prompt-panel">
                        <mat-expansion-panel-header>
                          <mat-panel-title class="discovery-prompt-panel-title">
                            <span class="kind-pill" [class.kind-generic]="item.kindLabel === 'Generic'"
                              [class.kind-branded]="item.kindLabel === 'Branded'"
                              [class.kind-competitor]="item.kindLabel === 'Competitor'">{{ item.kindLabel }}</span>
                            @if (item.competitorName) {
                              <span class="competitor-tag">{{ item.competitorName }}</span>
                            }
                            <span class="prompt-title-text">{{ item.query }}</span>
                            <span class="platform-pill">{{ item.platformLabel }}</span>
                          </mat-panel-title>
                        </mat-expansion-panel-header>
                        <div class="discovery-panel-body">
                          @if (item.mentioned_brands.length) {
                            <div class="prompt-mentions-inline">
                              <span class="meta-label">Mentions:</span> {{ item.mentioned_brands.join(', ') }}
                            </div>
                          }
                          <div class="response-area">
                            @if (isDiscoveryResponseFull(citation.id, item.accordionIndex) || !item.needsMore) {
                              <div class="response-full whitespace-pre-wrap">{{ item.answer_text }}</div>
                              @if (item.needsMore) {
                                <button type="button" class="link-like" (click)="setDiscoveryResponseFull(citation.id, item.accordionIndex, false)">less</button>
                              }
                            } @else {
                              <div class="response-preview whitespace-pre-wrap">{{ item.preview }}</div>
                              <button type="button" class="link-like" (click)="setDiscoveryResponseFull(citation.id, item.accordionIndex, true)">more</button>
                            }
                          </div>
                          @if (item.citations.length > 0) {
                            <div class="citation-chips-row">
                              <span class="citation-chips-label">Sources</span>
                              <div class="citation-chips">
                                @for (c of item.citations; track c.url) {
                                  <a class="citation-chip" [href]="c.url" target="_blank" rel="noopener noreferrer"
                                    [matTooltip]="c.url"><span class="chip-index">{{ c.chipIndex }}</span>{{ c.title }}</a>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      </mat-expansion-panel>
                    }
                  </mat-accordion>
                </div>
                }
              } @else if (loadingDetailId === citation.id) {
                <div class="content-section"><mat-spinner diameter="24"></mat-spinner></div>
              }
            </div>
          </mat-expansion-panel>
        }
      </mat-accordion>

      <!-- Single export menu (shared by all Export buttons) -->
      <mat-menu #exportMenu="matMenu">
        <button mat-menu-item (click)="exportingCitation && exportCitationAsCsv(exportingCitation)">
          <span>CSV</span>
        </button>
        <button mat-menu-item (click)="exportingCitation && exportCitationAsTxt(exportingCitation)">
          <span>TXT</span>
        </button>
      </mat-menu>

      @if (citations.length === 0) {
        <div class="empty-state">
          <mat-icon>folder_open</mat-icon>
          <p>No discovery citations stored yet.</p>
          <p class="hint">Citations are stored when AI Visibility metrics are calculated for a brand.</p>
        </div>
      }

      @if (totalPages > 1) {
        <div class="pagination">
          <button
            mat-button
            [disabled]="currentPage === 1"
            (click)="goToPage(currentPage - 1)">
            <mat-icon>chevron_left</mat-icon>
            Previous
          </button>
          <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            mat-button
            [disabled]="currentPage === totalPages"
            (click)="goToPage(currentPage + 1)">
            Next
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>
      }
    </div>
  }
</div>
